// src/services/api/client.ts

import { apiConfig } from './config';
import { ApiResponse, ApiError } from './types';
import { mockInterceptor } from '../mock/mockInterceptor';

export interface RequestConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  endpoint: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
}

export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    this.baseURL = apiConfig.baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    // SECURITY: Tokens are now stored in HTTP-only cookies, not localStorage
  }

  // Set auth token for all requests (kept for backwards compatibility during transition)
  setAuthToken(token: string | null) {
    if (token) {
      this.defaultHeaders['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.defaultHeaders['Authorization'];
    }
  }

  // Build query parameters
  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return '';
    
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    
    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  // Main request method
  async request<T = any>(config: RequestConfig): Promise<ApiResponse<T>> {
    const { method, endpoint, data, params, headers = {} } = config;

    // If using mock, intercept the request
    if (apiConfig.useMock) {
      return mockInterceptor.handleRequest<T>(config);
    }

    const url = `${this.baseURL}${endpoint}${this.buildQueryString(params)}`;
    const requestHeaders = { ...this.defaultHeaders, ...headers };
    
    try {
      // SECURITY: Include credentials to send HTTP-only cookies
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: data ? JSON.stringify(data) : undefined,
        signal: AbortSignal.timeout(apiConfig.timeout),
        credentials: 'include',
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle 401 Unauthorized - token might be expired
        if (response.status === 401) {
          // SECURITY: Clear session data (tokens are in HTTP-only cookies)
          localStorage.removeItem('globalUser');
          localStorage.removeItem('token_expires_at');
          // Redirect to login
          window.location.href = '/';
        }
        
        const error: ApiError = {
          success: false,
          message: responseData.detail || responseData.message || responseData.error || `HTTP ${response.status}`,
          errors: responseData.errors,
          status: response.status,
        };
        throw error;
      }

      // Backend returns the data directly, not wrapped in ApiResponse
      // So we need to wrap it in the expected format
      return {
        success: true,
        data: responseData
      } as ApiResponse<T>;
    } catch (error) {
      // Handle network errors
      if (error instanceof Error) {
        const apiError: ApiError = {
          success: false,
          message: error.message || 'Network error occurred',
          status: 0,
        };
        throw apiError;
      }
      
      // Re-throw API errors
      throw error;
    }
  }

  // Convenience methods
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'GET', endpoint, params });
  }

  async post<T = any>(endpoint: string, data?: any, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'POST', endpoint, data, params });
  }

  async put<T = any>(endpoint: string, data?: any, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PUT', endpoint, data, params });
  }

  async patch<T = any>(endpoint: string, data?: any, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PATCH', endpoint, data, params });
  }

  async delete<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'DELETE', endpoint, params });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export convenience function for making requests with error handling
export async function makeApiRequest<T>(
  requestFn: () => Promise<ApiResponse<T>>
): Promise<T> {
  try {
    const response = await requestFn();
    return response.data;
  } catch (error) {
    throw error;
  }
}
